import { t } from "../../../i18n";
import { discoverForms } from "./discovery";
import type { AppliedChange, BridgeValue, DiscoveredField, DiscoveredForm, FieldOption, FieldScalar } from "./types";

const MODIFIED_ATTRIBUTE = "data-ai-modified";
// A Bootstrap custom checkbox's row is its `.custom-control` wrapper: it has no `.form-group`, also
// when the kit FormGroup renders it with only the custom-control classes.
const ROW_SELECTOR = ".form-group, .custom-control";

/*
 * The bridge's own `input` / `change` dispatches reach the same capture listeners as the admin's
 * typing. Browsers mark synthetic events `isTrusted === false`, but jsdom never sets `isTrusted`,
 * so the bridge flags its own dispatch window instead — a strict superset of the `isTrusted`
 * check that behaves the same in tests and in browsers.
 */
let applyingDepth = 0;

export function withApplying<T>(action: () => T): T {
  applyingDepth += 1;
  try {
    return action();
  } finally {
    applyingDepth -= 1;
  }
}

export function isApplying(): boolean {
  return applyingDepth > 0;
}

/** A value that holds nothing: null, "" or an empty list. */
export function isEmptyValue(value: BridgeValue): boolean {
  return value == null || value === "" || (Array.isArray(value) && value.length === 0);
}

function optionLabel(value: FieldScalar, options: FieldOption[] | undefined): string {
  const option = options?.find((candidate) => valuesEqual(candidate.value, value));
  return option?.label || String(value);
}

/**
 * A field value as the admin sees it: the option label for select-like fields (the raw value when
 * no option matches), a list as its elements. An empty value reads as empty even on a select: its
 * null option's label is a placeholder such as "Select value", which would read as if something
 * had been chosen.
 */
export function formatForDisplay(value: BridgeValue, options?: FieldOption[]): string {
  if (value === undefined || isEmptyValue(value)) {
    return t("ai-chat.forms.empty", undefined, "(empty)");
  }
  if (Array.isArray(value)) {
    return value.map((element) => optionLabel(element, options)).join(", ");
  }
  return optionLabel(value, options);
}

function elementSet(value: BridgeValue): Set<string> {
  if (Array.isArray(value)) {
    return new Set(value.map(String));
  }
  return isEmptyValue(value) ? new Set() : new Set([String(value)]);
}

/**
 * Loose, stringified equality: "30" and 30 are the same field value, as are null and "". A list
 * compares as a set — a native multi-select reads back in option order, not in the order it was
 * set — and an empty list equals an empty value.
 */
export function valuesEqual(left: BridgeValue, right: BridgeValue): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftSet = elementSet(left);
    const rightSet = elementSet(right);
    return leftSet.size === rightSet.size && Array.from(leftSet).every((element) => rightSet.has(element));
  }
  const normalize = (value: BridgeValue): string => (value == null ? "" : String(value));
  return normalize(left) === normalize(right);
}

/**
 * Marks the element (and its row) as AI-set, with the previous value as tooltip — by option label
 * when the field has options — and a description for assistive tech.
 */
export function markField(element: HTMLElement, previousValue: BridgeValue, options?: FieldOption[]): void {
  element.setAttribute(MODIFIED_ATTRIBUTE, "true");
  // An attribute value is never parsed as markup, so i18next escaping would only show "&" as "&amp;" in the tooltip.
  const previously = t(
    "ai-chat.forms.previously",
    { value: formatForDisplay(previousValue, options), interpolation: { escapeValue: false } },
    "Previously: {{value}}",
  );
  element.setAttribute("title", previously);
  element.setAttribute("aria-description", t("ai-chat.forms.set-by-ai", undefined, "Set by AI assistant"));
  element.closest(ROW_SELECTOR)?.setAttribute(MODIFIED_ATTRIBUTE, "true");
}

/** Removes the marker from the element, and from its row once no marked field is left in it. */
export function unmarkField(element: HTMLElement): void {
  element.removeAttribute(MODIFIED_ATTRIBUTE);
  element.removeAttribute("title");
  element.removeAttribute("aria-description");
  const group = element.closest(ROW_SELECTOR);
  if (group && !group.querySelector(`[${MODIFIED_ATTRIBUTE}]`)) {
    group.removeAttribute(MODIFIED_ATTRIBUTE);
  }
}

/** Removes every marker under the root, fields and rows alike. */
export function clearMarkers(root: ParentNode): void {
  Array.from(root.querySelectorAll<HTMLElement>(`[${MODIFIED_ATTRIBUTE}]`)).forEach((element) => {
    element.removeAttribute(MODIFIED_ATTRIBUTE);
    if (!element.matches(ROW_SELECTOR)) {
      element.removeAttribute("title");
      element.removeAttribute("aria-description");
    }
  });
}

function findField(forms: DiscoveredForm[], change: AppliedChange): DiscoveredField | undefined {
  return forms.find((form) => form.formId === change.formId)?.fields.find((field) => field.path === change.path);
}

function fieldKey(change: AppliedChange): string {
  return `${change.formId}\u0000${change.path}`;
}

/**
 * The changes the admin has overridden: every change to a field whose rendered value is no longer
 * what the assistant last set it to. A field set twice counts by its latest change, so an
 * earlier value replaced by the assistant itself is not superseded. A field that has left the
 * page is kept — it may be re-rendered and still hold the value.
 */
export function supersededChanges(changes: AppliedChange[], forms: DiscoveredForm[]): AppliedChange[] {
  const latest = new Map<string, AppliedChange>();
  changes.forEach((change) => latest.set(fieldKey(change), change));
  const overridden = new Set<string>();
  latest.forEach((change, key) => {
    const field = findField(forms, change);
    if (field && !valuesEqual(field.adapter.currentValue(), change.appliedValue)) {
      overridden.add(key);
    }
  });
  return changes.filter((change) => overridden.has(fieldKey(change)));
}

/**
 * Makes the markers on the page match the changes: a field is marked exactly while it still
 * holds the value the assistant set. This re-marks an element React re-created and clears a
 * field the admin edited by hand. Later changes to the same field win over earlier ones.
 */
export function reconcileMarkers(changes: AppliedChange[], root: ParentNode = document, forms: DiscoveredForm[] = discoverForms(root)): void {
  const live = new Map<HTMLElement, { previousValue: BridgeValue; field: DiscoveredField }>();
  changes.forEach((change) => {
    const field = findField(forms, change);
    const element = field?.adapter.element;
    if (!field || !element) {
      return;
    }
    if (valuesEqual(field.adapter.currentValue(), change.appliedValue)) {
      live.set(element, { previousValue: change.previousValue, field });
    } else {
      live.delete(element);
    }
  });
  clearMarkers(root);
  live.forEach(({ previousValue, field }, element) => markField(element, previousValue, field.adapter.options()));
}
